import React, { FC, memo, useCallback, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import {
  Alert,
  Autocomplete,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { FixedSizeList, ListChildComponentProps } from "react-window";
import { Download, HelpCircle, Upload } from "feather-icons-react";

import { JobSeniorityCombination, PositionAnswersMap, PositionListItem } from "../../../../hooks";
import { PositionQuestionnaireDialog } from "./PositionQuestionnaireDialog";

import { Button } from "@components/base";

const PARENT_KEYS = ["1", "2", "3", "4"] as const;
const ROW_HEIGHT = 56;
const MAX_LIST_HEIGHT = 360;
const HEADER_GRID_TEMPLATE = "44px 1.2fr 0.8fr 1.5fr repeat(4, 1fr) 0.5fr 0.7fr 0.7fr";
const CELL_PADDING = 6;

const normalizeDecimal = (value: string | number): number | null => {
  const normalized =
    typeof value === "string" ? value.replace(",", ".") : String(value);
  const parsed = Number(normalized);


return Number.isNaN(parsed) ? null : parsed;
};

interface RowData {
  uniqueJobSeniorities: JobSeniorityCombination[];
  comboPoints: Record<string, Record<string, number>>;
  onPointChange: (comboKey: string, parentId: string, value: number) => void;
  onSalaryChange: (
    comboKey: string,
    field: "salaryMin" | "salaryMax",
    value: number,
  ) => void;
  getGradeForPoints: (sum: number) => string;
  onOpenQuestionnaire: (combo: JobSeniorityCombination) => void;
  hasAnswers: (comboKey: string) => boolean;
  openTooltip: string;
  parentTotals: Record<string, number>;
  positionList: PositionListItem[];
  jobTitleMatches: Record<string, string | null>;
  matchedGrades: Record<string, number | null>;
  onPositionSelect: (
    comboKey: string,
    jobTitle: string,
    position: PositionListItem | null,
  ) => void;
  positionLabel: string;
}

const Row: FC<ListChildComponentProps<RowData>> = ({ index, style, data }) => {
  const {
    uniqueJobSeniorities,
    comboPoints,
    onPointChange,
    onSalaryChange,
    getGradeForPoints,
    onOpenQuestionnaire,
    hasAnswers,
    openTooltip,
    parentTotals,
    positionList,
    jobTitleMatches,
    matchedGrades,
    onPositionSelect,
    positionLabel,
  } = data;
  const combo = uniqueJobSeniorities[index];
  const current = comboPoints[combo.key] || {};
  const isPrefilled = hasAnswers(combo.key);

  const sum = PARENT_KEYS.reduce(
    (acc, parentId) => acc + (current[parentId] || 0),
    0,
  );

  const selectedPositionId = jobTitleMatches[combo.key];
  const selectedPosition = selectedPositionId
    ? positionList.find((p) => p.id === selectedPositionId) ?? null
    : null;

  const gradeDisplay =
    matchedGrades[combo.key] != null
      ? String(matchedGrades[combo.key])
      : getGradeForPoints(sum);

  return (
    <Box
      style={style}
      display="grid"
      gridTemplateColumns={HEADER_GRID_TEMPLATE}
      alignItems="center"
      gap={0.5}
      px={1}
      sx={{
        borderTop: "1px solid",
        borderColor: "grey.100",
        backgroundColor: index % 2 === 0 ? "white" : "grey.50",
        fontSize: "0.85rem",
        boxSizing: "border-box",
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="center">
        <Tooltip title={openTooltip}>
          <IconButton
            size="small"
            onClick={(): void => onOpenQuestionnaire(combo)}
            aria-label={openTooltip}
          >
            <HelpCircle
              size={16}
              color={isPrefilled ? "var(--mui-palette-primary-main)" : undefined}
            />
          </IconButton>
        </Tooltip>
      </Box>
      <Box
        sx={{
          whiteSpace: "normal",
          wordBreak: "break-word",
          px: `${CELL_PADDING}px`,
        }}
      >
        {combo.jobTitle}
      </Box>
      <Box
        sx={{
          whiteSpace: "normal",
          wordBreak: "break-word",
          px: `${CELL_PADDING}px`,
        }}
      >
        {combo.seniority}
      </Box>
      <Autocomplete
        size="small"
        options={positionList}
        value={selectedPosition}
        onChange={(_, newValue): void =>
          onPositionSelect(combo.key, combo.jobTitle, newValue)
        }
        getOptionLabel={(option): string => option.jobTitle}
        isOptionEqualToValue={(option, value): boolean => option.id === value.id}
        filterOptions={(options, { inputValue }): PositionListItem[] => {
          const term = inputValue.toLowerCase();

          return options.filter(
            (pos) =>
              pos.jobTitle.toLowerCase().includes(term) ||
              pos.similarJobTitles.toLowerCase().includes(term),
          );
        }}
        renderOption={(props, option): React.ReactElement => (
          <Box component="li" {...props} key={option.id}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
                {option.jobTitle}
              </Typography>
              {option.similarJobTitles && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ lineHeight: 1.2 }}
                >
                  {option.similarJobTitles}
                </Typography>
              )}
            </Box>
          </Box>
        )}
        renderInput={(params): React.ReactElement => (
          <TextField
            {...params}
            variant="filled"
            placeholder={positionLabel}
            sx={{
              "& .MuiFilledInput-root": {
                py: 0,
                minHeight: 36,
              },
              "& .MuiFilledInput-input": {
                py: "8px",
                px: "8px",
                fontSize: "0.8rem",
              },
            }}
          />
        )}
        sx={{ minWidth: 0 }}
      />
      {PARENT_KEYS.map((parentId) => {
        const maxValue = parentTotals[parentId] || 999;


return (
          <TextField
            key={`${combo.key}-${parentId}`}
            size="small"
            type="number"
            variant="filled"
            value={current[parentId] ?? ""}
            slotProps={{
              htmlInput: { min: 0, max: maxValue },
            }}
            onWheel={(e): void => (e.target as HTMLInputElement).blur()}
            onChange={(e): void => {
              const parsed = normalizeDecimal(e.target.value);
              const safeVal = parsed === null ? 0 : parsed;
              const val = Math.min(Math.max(0, safeVal), maxValue);

              onPointChange(combo.key, parentId, val);
            }}
          />
        );
      })}
      <Box
        sx={{
          fontWeight: 600,
          px: `${CELL_PADDING}px`,
          color: matchedGrades[combo.key] != null ? "primary.main" : undefined,
        }}
      >
        {gradeDisplay}
      </Box>
      <TextField
        size="small"
        type="number"
        variant="filled"
        value={current.salaryMin ?? ""}
        slotProps={{
          htmlInput: { min: 0 },
        }}
        onWheel={(e): void => (e.target as HTMLInputElement).blur()}
        onChange={(e): void => {
          const parsed = normalizeDecimal(e.target.value);
          const safeVal = parsed === null ? 0 : Math.max(0, parsed);

          onSalaryChange(combo.key, "salaryMin", safeVal);
        }}
      />
      <TextField
        size="small"
        type="number"
        variant="filled"
        value={current.salaryMax ?? ""}
        slotProps={{
          htmlInput: { min: 0 },
        }}
        onWheel={(e): void => (e.target as HTMLInputElement).blur()}
        onChange={(e): void => {
          const parsed = normalizeDecimal(e.target.value);
          const safeVal = parsed === null ? 0 : Math.max(0, parsed);

          onSalaryChange(combo.key, "salaryMax", safeVal);
        }}
      />
    </Box>
  );
};

Row.displayName = "JobSeniorityRow";

interface JobSeniorityPointsTabProps {
  uniqueJobSeniorities: JobSeniorityCombination[];
  comboPoints: Record<string, Record<string, number>>;
  setComboPoints: React.Dispatch<
    React.SetStateAction<Record<string, Record<string, number>>>
  >;
  questionnaireAnswers: Record<string, PositionAnswersMap>;
  setQuestionnaireAnswers: React.Dispatch<
    React.SetStateAction<Record<string, PositionAnswersMap>>
  >;
  traitMaxValues: Record<string, number>;
  parentTotals: Record<string, number>;
  totalMaxPoints: number;
  isLoadingPersonnel: boolean;
  getGradeForPoints: (sum: number) => string;
  onApplyGrades: () => void;
  onSaveQuestionnaireAnswers: (
    comboKey: string,
    newAnswers: PositionAnswersMap,
    updatedComboPoints: Record<string, Record<string, number>>,
  ) => void;
  // Position list props
  positionList: PositionListItem[];
  setPositionList: React.Dispatch<React.SetStateAction<PositionListItem[]>>;
  onSavePositionList: (list: PositionListItem[]) => void;
  onSaveJobTitleMatches: (
    updatedJobTitleMatches: Record<string, string | null>,
    updatedMatchedGrades: Record<string, number | null>,
  ) => void;
  jobTitleMatches: Record<string, string | null>;
  setJobTitleMatches: React.Dispatch<React.SetStateAction<Record<string, string | null>>>;
  matchedGrades: Record<string, number | null>;
  setMatchedGrades: React.Dispatch<React.SetStateAction<Record<string, number | null>>>;
  // Highlight props
  highlightOverwriteButton?: boolean;
}

export const JobSeniorityPointsTab: FC<JobSeniorityPointsTabProps> = memo(
  ({
    uniqueJobSeniorities,
    comboPoints,
    setComboPoints,
    questionnaireAnswers,
    setQuestionnaireAnswers,
    traitMaxValues,
    parentTotals,
    totalMaxPoints,
    isLoadingPersonnel,
    getGradeForPoints,
    onApplyGrades,
    onSaveQuestionnaireAnswers,
    positionList,
    setPositionList,
    onSavePositionList,
    onSaveJobTitleMatches,
    jobTitleMatches,
    setJobTitleMatches,
    matchedGrades,
    setMatchedGrades,
    highlightOverwriteButton = false,
  }) => {
    const { t } = useTranslation();
    const questionnaireLabel = t(
      "paygap.gradeEstimator.openQuestionnaire",
      "Open questionnaire",
    );
    const positionSearchLabel = t(
      "paygap.gradeEstimator.searchPosition",
      "Search position...",
    );
    const [activeCombo, setActiveCombo] = useState<JobSeniorityCombination | null>(
      null,
    );
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const handlePositionSelect = useCallback(
      (
        comboKey: string,
        jobTitle: string,
        position: PositionListItem | null,
      ): void => {
        if (position) {
          // Set the match and auto-fill grade
          const updatedJobTitleMatches = { ...jobTitleMatches, [comboKey]: position.id };
          const updatedMatchedGrades = { ...matchedGrades, [comboKey]: position.grade };

          setJobTitleMatches(updatedJobTitleMatches);
          setMatchedGrades(updatedMatchedGrades);
          onSaveJobTitleMatches(updatedJobTitleMatches, updatedMatchedGrades);

          // If job title doesn't match the position's jobTitle and isn't already
          // in the position's similarJobTitles, add it
          const similarTitles = position.similarJobTitles
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);

          if (
            position.jobTitle !== jobTitle &&
            !similarTitles.includes(jobTitle)
          ) {
            const updatedSimilar = position.similarJobTitles
              ? `${position.similarJobTitles}, ${jobTitle}`
              : jobTitle;

            const updatedList = positionList.map((p) =>
              p.id === position.id
                ? { ...p, similarJobTitles: updatedSimilar }
                : p,
            );

            setPositionList(updatedList);
            onSavePositionList(updatedList);
          }
        } else {
          // Clear the match
          const updatedJobTitleMatches = { ...jobTitleMatches, [comboKey]: null };
          const updatedMatchedGrades = { ...matchedGrades, [comboKey]: null };

          setJobTitleMatches(updatedJobTitleMatches);
          setMatchedGrades(updatedMatchedGrades);
          onSaveJobTitleMatches(updatedJobTitleMatches, updatedMatchedGrades);
        }
      },
      [
        jobTitleMatches,
        matchedGrades,
        positionList,
        setJobTitleMatches,
        setMatchedGrades,
        setPositionList,
        onSavePositionList,
        onSaveJobTitleMatches,
      ],
    );

    const handlePointChange = useCallback(
      (comboKey: string, parentId: string, value: number): void => {
        const capped = Math.max(0, Math.min(value, parentTotals[parentId] ?? value));

        setComboPoints((prev) => ({
          ...prev,
          [comboKey]: {
            ...(prev[comboKey] || {}),
            [parentId]: capped,
          },
        }));
      },
      [parentTotals, setComboPoints],
    );

    const handleSalaryChange = useCallback(
      (comboKey: string, field: "salaryMin" | "salaryMax", value: number): void => {
        setComboPoints((prev) => ({
          ...prev,
          [comboKey]: {
            ...(prev[comboKey] || {}),
            [field]: value,
          },
        }));
      },
      [setComboPoints],
    );

    const computeParentScores = useCallback(
      (answers: PositionAnswersMap): Record<string, number> => {
        const totals: Record<string, number> = {};

        Object.entries(answers).forEach(([questionCode, answer]) => {
          const rating = Number(answer.rating);

          if (!Number.isFinite(rating) || rating <= 0) return;

          const parentId = questionCode.split(".")[0];
          const parentMax = parentTotals[parentId] ?? 0;
          const childMax =
            traitMaxValues[questionCode] !== undefined
              ? traitMaxValues[questionCode]
              : parentMax;
          const childWeight = parentMax > 0 ? childMax / parentMax : 0;
          // user rating (1–10) -> percent * weight * parentMax
          const contribution = (rating / 10) * childWeight * parentMax;

          totals[parentId] = Math.min(
            parentMax,
            (totals[parentId] ?? 0) + contribution,
          );
        });

        // Round final totals to whole numbers while keeping caps
        Object.keys(totals).forEach((parentId) => {
          const parentMax = parentTotals[parentId] ?? 0;

          totals[parentId] = Math.min(parentMax, Math.round(totals[parentId] ?? 0));
        });

        return totals;
      },
      [parentTotals, traitMaxValues],
    );

    const handleSubmitQuestionnaire = useCallback(
      (answers: PositionAnswersMap): void => {
        if (!activeCombo) return;

        // Compute scores from answers for local state update
        const parentScores = computeParentScores(answers);

        // Update local state
        setQuestionnaireAnswers((prev) => ({
          ...prev,
          [activeCombo.key]: answers,
        }));

        // Compute the updated comboPoints synchronously so we can save it
        const updatedComboPoints = {
          ...comboPoints,
          [activeCombo.key]: {
            ...(comboPoints[activeCombo.key] || {}),
            ...parentScores,
          },
        };

        setComboPoints(updatedComboPoints);

        // Save questionnaire answers and computed comboPoints to the API
        onSaveQuestionnaireAnswers(activeCombo.key, answers, updatedComboPoints);

        setIsDialogOpen(false);
        setActiveCombo(null);
      },
      [
        activeCombo,
        computeParentScores,
        comboPoints,
        setComboPoints,
        setQuestionnaireAnswers,
        onSaveQuestionnaireAnswers,
      ],
    );

    const handleOpenQuestionnaire = useCallback((combo: JobSeniorityCombination) => {
      setActiveCombo(combo);
      setIsDialogOpen(true);
    }, []);

    const handleCloseDialog = useCallback(() => {
      setIsDialogOpen(false);
      setActiveCombo(null);
    }, []);

    const handleExportToExcel = useCallback((): void => {
      // Build headers
      const headers = [
        t("paygap.gradeEstimator.jobTitle", "Job title"),
        t("paygap.gradeEstimator.seniority", "Seniority"),
        t("paygap.gradeEstimator.positionListMatch", "Position"),
        `1. ${t("paygap.gradeEstimator.parent1", "Complexity (skills)").replace(/^\s*\d+[.)]?\s*/, "")}`,
        `2. ${t("paygap.gradeEstimator.parent2", "Effort").replace(/^\s*\d+[.)]?\s*/, "")}`,
        `3. ${t("paygap.gradeEstimator.parent3", "Responsibility").replace(/^\s*\d+[.)]?\s*/, "")}`,
        `4. ${t("paygap.gradeEstimator.parent4", "Working conditions").replace(/^\s*\d+[.)]?\s*/, "")}`,
        t("paygap.gradeEstimator.gradeColumn", "Grade"),
        t("paygap.gradeEstimator.salaryRangeMin"),
        t("paygap.gradeEstimator.salaryRangeMax"),
      ];

      // Build data rows
      const rows = uniqueJobSeniorities.map((combo) => {
        const current = comboPoints[combo.key] || {};
        const sum = PARENT_KEYS.reduce(
          (acc, parentId) => acc + (current[parentId] || 0),
          0,
        );

        const matchedPositionId = jobTitleMatches[combo.key];
        const matchedPosition = matchedPositionId
          ? positionList.find((p) => p.id === matchedPositionId)
          : null;
        const gradeDisplay =
          matchedGrades[combo.key] != null
            ? String(matchedGrades[combo.key])
            : getGradeForPoints(sum);


return [
          combo.jobTitle,
          combo.seniority,
          matchedPosition?.jobTitle ?? "",
          current["1"] ?? "",
          current["2"] ?? "",
          current["3"] ?? "",
          current["4"] ?? "",
          gradeDisplay,
          current.salaryMin ?? "",
          current.salaryMax ?? "",
        ];
      });

      // Create worksheet
      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        t("paygap.gradeEstimator.excelSheetName", "Grade Points"),
      );

      // Export
      const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([wbout], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.href = url;
      link.download = `grade_estimator_${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, [uniqueJobSeniorities, comboPoints, getGradeForPoints, t, jobTitleMatches, matchedGrades, positionList]);

    // Export questionnaire answers to Excel
    const handleExportQuestionnaires = useCallback((): void => {
      const headers = [
        t("paygap.gradeEstimator.jobTitle", "Job title"),
        t("paygap.gradeEstimator.seniority", "Seniority"),
        t("paygap.gradeEstimator.questionCode", "Question Code"),
        t("paygap.gradeEstimator.rating", "Rating (1-10)"),
        t("paygap.gradeEstimator.comment", "Comment"),
      ];

      const rows: (string | number)[][] = [];

      // Iterate through all positions with questionnaire answers
      uniqueJobSeniorities.forEach((combo) => {
        const answers = questionnaireAnswers[combo.key];

        if (!answers) return;

        Object.entries(answers).forEach(([questionCode, answer]) => {
          rows.push([
            combo.jobTitle,
            combo.seniority,
            questionCode,
            answer.rating ?? "",
            answer.comment ?? "",
          ]);
        });
      });

      if (rows.length === 0) {
        // No questionnaire data to export
        return;
      }

      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        t("paygap.gradeEstimator.questionnaireSheetName", "Questionnaires"),
      );

      const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([wbout], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.href = url;
      link.download = `questionnaire_answers_${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, [uniqueJobSeniorities, questionnaireAnswers, t]);

    // Check if there are any questionnaire answers to export
    const hasQuestionnaireData = useMemo(() => {
      return Object.values(questionnaireAnswers).some(
        (answers) => answers && Object.keys(answers).length > 0,
      );
    }, [questionnaireAnswers]);

    // Import questionnaire state and handlers
    const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
    const [pendingImportData, setPendingImportData] = useState<Record<string, PositionAnswersMap> | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleImportClick = useCallback((): void => {
      fileInputRef.current?.click();
    }, []);

    const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
      const file = event.target.files?.[0];

      if (!file) return;

      const reader = new FileReader();

      reader.onload = (e): void => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json<{
            [key: string]: string | number;
          }>(worksheet);

          // Build a map of position answers from the imported data
          const importedAnswers: Record<string, PositionAnswersMap> = {};

          // Get header keys from the first row's keys
          const firstRow = jsonData[0];

          if (!firstRow) return;

          // Find the column keys (they might be translated)
          const keys = Object.keys(firstRow);
          // Assume columns are in order: Job Title, Seniority, Question Code, Rating, Comment
          const jobTitleKey = keys[0];
          const seniorityKey = keys[1];
          const questionCodeKey = keys[2];
          const ratingKey = keys[3];
          const commentKey = keys[4];

          jsonData.forEach((row) => {
            const jobTitle = String(row[jobTitleKey] || "").trim();
            const seniority = String(row[seniorityKey] || "").trim();
            const questionCode = String(row[questionCodeKey] || "").trim();
            const rating = row[ratingKey] !== undefined && row[ratingKey] !== ""
              ? Number(row[ratingKey])
              : undefined;
            const comment = row[commentKey] !== undefined
              ? String(row[commentKey])
              : undefined;

            if (!jobTitle || !seniority || !questionCode) return;

            // Use the same separator as uniqueJobSeniorities: "|||"
            const comboKey = `${jobTitle}|||${seniority}`;

            if (!importedAnswers[comboKey]) {
              importedAnswers[comboKey] = {};
            }

            importedAnswers[comboKey][questionCode] = {
              rating,
              comment,
            };
          });

          // Store the parsed data and show confirmation dialog
          setPendingImportData(importedAnswers);
          setIsImportDialogOpen(true);
        } catch (error) {
          console.error("Error parsing import file:", error);
        }
      };
      reader.readAsArrayBuffer(file);

      // Reset the input value so the same file can be selected again
      if (event.target) {
        event.target.value = "";
      }
    }, []);

    const handleConfirmImport = useCallback((): void => {
      if (!pendingImportData) return;

      // Build up the merged state first
      const mergedAnswers = { ...questionnaireAnswers };
      const updatedComboPoints = { ...comboPoints };

      // Process each imported combo
      Object.entries(pendingImportData).forEach(([comboKey, answers]) => {
        // Find the matching combo to ensure it exists
        const combo = uniqueJobSeniorities.find((c) => c.key === comboKey);

        if (!combo) return;

        // Compute scores from answers
        const parentScores = computeParentScores(answers);

        // Update local tracking
        mergedAnswers[comboKey] = answers;
        updatedComboPoints[comboKey] = {
          ...(updatedComboPoints[comboKey] || {}),
          ...parentScores,
        };
      });

      // Update local state
      setQuestionnaireAnswers(mergedAnswers);
      setComboPoints(updatedComboPoints);

      // Save each imported combo individually
      if (onSaveQuestionnaireAnswers) {
        Object.entries(pendingImportData).forEach(([comboKey, answers]) => {
          const combo = uniqueJobSeniorities.find((c) => c.key === comboKey);

          if (!combo) return;

          onSaveQuestionnaireAnswers(comboKey, answers, updatedComboPoints);
        });
      }

      setPendingImportData(null);
      setIsImportDialogOpen(false);
    }, [
      pendingImportData,
      uniqueJobSeniorities,
      computeParentScores,
      setQuestionnaireAnswers,
      setComboPoints,
      questionnaireAnswers,
      comboPoints,
      onSaveQuestionnaireAnswers,
    ]);

    const handleCancelImport = useCallback((): void => {
      setPendingImportData(null);
      setIsImportDialogOpen(false);
    }, []);

    const itemData: RowData = {
      uniqueJobSeniorities,
      comboPoints,
      onPointChange: handlePointChange,
      onSalaryChange: handleSalaryChange,
      getGradeForPoints,
      onOpenQuestionnaire: handleOpenQuestionnaire,
      hasAnswers: (comboKey: string): boolean =>
        Boolean(questionnaireAnswers[comboKey]),
      openTooltip: questionnaireLabel,
      parentTotals,
      positionList,
      jobTitleMatches,
      matchedGrades,
      onPositionSelect: handlePositionSelect,
      positionLabel: positionSearchLabel,
    };

    const listHeight = Math.min(
      uniqueJobSeniorities.length * ROW_HEIGHT,
      MAX_LIST_HEIGHT,
    );

    return (
      <Box display="flex" flexDirection="column" gap={1}>
        {isLoadingPersonnel && (
          <Typography variant="caption" color="text.secondary">
            {t("paygap.gradeEstimator.loadingData")}
          </Typography>
        )}
        <Box
          sx={{
            border: "1px solid",
            borderColor: "grey.200",
            borderRadius: 1,
          }}
        >
          <Box
            display="grid"
            gridTemplateColumns={HEADER_GRID_TEMPLATE}
            alignItems="center"
            bgcolor="grey.100"
            gap={0.5}
            px={1}
            py={0.5}
            fontWeight={600}
            fontSize="0.8rem"
            sx={{
              "& > *": { whiteSpace: "normal", px: `${CELL_PADDING}px` },
            }}
          >
            <Box sx={{ textAlign: "center", px: 0 }}>
              {t("paygap.gradeEstimator.estimateColumn", "Estimate")}
            </Box>
            <Box>{t("paygap.gradeEstimator.jobTitle")}</Box>
            <Box>{t("paygap.gradeEstimator.seniority")}</Box>
            <Box>
              {t("paygap.gradeEstimator.positionListMatch", "Position")}
            </Box>
            <Box>
              1.{"\u00A0"}
              {t("paygap.gradeEstimator.parent1").replace(/^\s*\d+[.)]?\s*/, "")}
            </Box>
            <Box>
              2.{"\u00A0"}
              {t("paygap.gradeEstimator.parent2").replace(/^\s*\d+[.)]?\s*/, "")}
            </Box>
            <Box>
              3.{"\u00A0"}
              {t("paygap.gradeEstimator.parent3").replace(/^\s*\d+[.)]?\s*/, "")}
            </Box>
            <Box>
              4.{"\u00A0"}
              {t("paygap.gradeEstimator.parent4").replace(/^\s*\d+[.)]?\s*/, "")}
            </Box>
            <Box>{t("paygap.gradeEstimator.gradeColumn")}</Box>
            <Box>
              {t("paygap.gradeEstimator.salaryRangeMin")}
            </Box>
            <Box>
              {t("paygap.gradeEstimator.salaryRangeMax")}
            </Box>
          </Box>
          {uniqueJobSeniorities.length === 0 ? (
            <Box px={1} py={1} fontSize="0.9rem" color="text.secondary">
              {t("paygap.gradeEstimator.noData")}
            </Box>
          ) : (
            <FixedSizeList
              height={listHeight}
              itemCount={uniqueJobSeniorities.length}
              itemSize={ROW_HEIGHT}
              width="100%"
              itemData={itemData}
            >
              {Row}
            </FixedSizeList>
          )}
        </Box>
        <Stack direction="row" justifyContent="flex-end" spacing={1} mt={1} alignItems="center" flexWrap="wrap" gap={1}>
          <Button
            variant="outlined"
            startIcon={<Download size={16} />}
            onClick={handleExportToExcel}
            disabled={uniqueJobSeniorities.length === 0}
          >
            {t("paygap.gradeEstimator.exportExcel")}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download size={16} />}
            onClick={handleExportQuestionnaires}
            disabled={!hasQuestionnaireData}
          >
            {t("paygap.gradeEstimator.exportQuestionnaires", "Export Questionnaires")}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Upload size={16} />}
            onClick={handleImportClick}
          >
            {t("paygap.gradeEstimator.importQuestionnaires", "Import Questionnaires")}
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            accept=".xlsx,.xls"
            onChange={handleFileSelect}
          />
          <Button
            variant="contained"
            color={highlightOverwriteButton ? "warning" : "primary"}
            onClick={onApplyGrades}
            sx={highlightOverwriteButton ? {
              animation: "pulse 1.5s ease-in-out infinite",
              "@keyframes pulse": {
                "0%": { boxShadow: "0 0 0 0 rgba(237, 108, 2, 0.4)" },
                "70%": { boxShadow: "0 0 0 10px rgba(237, 108, 2, 0)" },
                "100%": { boxShadow: "0 0 0 0 rgba(237, 108, 2, 0)" },
              },
            } : undefined}
          >
            {t("paygap.gradeEstimator.applyGradesButton")}
          </Button>
        </Stack>
        <PositionQuestionnaireDialog
          open={isDialogOpen}
          combo={activeCombo}
          onClose={handleCloseDialog}
          onSubmit={handleSubmitQuestionnaire}
          parentTotals={parentTotals}
          existingAnswers={
            activeCombo ? questionnaireAnswers[activeCombo.key] : undefined
          }
        />

        {/* Import Confirmation Dialog */}
        <Dialog open={isImportDialogOpen} onClose={handleCancelImport}>
          <DialogTitle>
            {t("paygap.gradeEstimator.importConfirmTitle", "Import Questionnaires")}
          </DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t(
                "paygap.gradeEstimator.importWarning",
                "This action will overwrite existing questionnaire data!"
              )}
            </Alert>
            <Typography variant="body1" gutterBottom>
              {t(
                "paygap.gradeEstimator.importConfirmMessage",
                "Are you sure you want to import questionnaire data? This will overwrite any existing questionnaire answers for matching positions."
              )}
            </Typography>
            {pendingImportData && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {t(
                  "paygap.gradeEstimator.importPositionCount",
                  "{{count}} position(s) will be updated.",
                  { count: Object.keys(pendingImportData).length }
                )}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelImport} color="inherit">
              {t("common.cancel", "Cancel")}
            </Button>
            <Button onClick={handleConfirmImport} variant="contained" color="primary">
              {t("common.import", "Import")}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  },
);

JobSeniorityPointsTab.displayName = "JobSeniorityPointsTab";
